import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// ============== TYPES ==============

export interface ExportColumn {
  key: string;
  header: string;
  width?: number; // For Excel column width
  format?: (value: any) => string; // Custom formatter
}

export interface ExportOptions {
  filename: string;
  title?: string;
  subtitle?: string;
  columns: ExportColumn[];
  data: Record<string, any>[];
  dateRange?: { start: Date; end: Date };
}

// ============== EXCEL EXPORT ==============

export function exportToExcel(options: ExportOptions): void {
  const { filename, title, columns, data } = options;

  // Prepare data with headers
  const exportData = data.map((row) => {
    const exportRow: Record<string, any> = {};
    columns.forEach((col) => {
      const value = row[col.key];
      exportRow[col.header] = col.format ? col.format(value) : value ?? '';
    });
    return exportRow;
  });

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(exportData);

  // Set column widths
  worksheet['!cols'] = columns.map((col) => ({ wch: col.width || 15 }));

  // Create workbook
  const workbook = XLSX.utils.book_new();
  const sheetName = (title || 'Datos').substring(0, 31);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate filename with date
  const dateStr = format(new Date(), 'yyyy-MM-dd');
  const fullFilename = `${filename}_${dateStr}.xlsx`;

  // Download
  XLSX.writeFile(workbook, fullFilename);
}

// ============== CSV EXPORT ==============

export function exportToCSV(options: ExportOptions): void {
  const { filename, columns, data } = options;

  // Prepare data with headers
  const exportData = data.map((row) => {
    const exportRow: Record<string, any> = {};
    columns.forEach((col) => {
      const value = row[col.key];
      exportRow[col.header] = col.format ? col.format(value) : value ?? '';
    });
    return exportRow;
  });

  // Create worksheet and convert to CSV
  const worksheet = XLSX.utils.json_to_sheet(exportData);
  const csv = XLSX.utils.sheet_to_csv(worksheet);

  // Create blob and download
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const dateStr = format(new Date(), 'yyyy-MM-dd');
  link.download = `${filename}_${dateStr}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ============== PDF EXPORT ==============

export function exportToPDF(options: ExportOptions): void {
  const { filename, title, subtitle, columns, data, dateRange } = options;

  // Create PDF in landscape for tables
  const doc = new jsPDF({
    orientation: data.length > 0 && columns.length > 5 ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;

  // Title
  if (title) {
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(title, pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;
  }

  // Subtitle
  if (subtitle) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(subtitle, pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
  }

  // Date range
  if (dateRange) {
    doc.setFontSize(10);
    doc.setTextColor(100);
    const rangeText = `Periodo: ${format(dateRange.start, "d 'de' MMMM yyyy", { locale: es })} - ${format(dateRange.end, "d 'de' MMMM yyyy", { locale: es })}`;
    doc.text(rangeText, pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
  }

  // Generation date
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(`Generado: ${format(new Date(), "dd/MM/yyyy HH:mm")}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 10;

  // Reset text color
  doc.setTextColor(0);

  // Prepare table data
  const tableHeaders = columns.map((col) => col.header);
  const tableData = data.map((row) =>
    columns.map((col) => {
      const value = row[col.key];
      return col.format ? col.format(value) : value ?? '';
    })
  );

  // Generate table
  autoTable(doc, {
    startY: yPos,
    head: [tableHeaders],
    body: tableData,
    theme: 'striped',
    headStyles: {
      fillColor: [59, 130, 246], // Blue-500
      textColor: 255,
      fontStyle: 'bold',
      halign: 'center',
    },
    bodyStyles: {
      fontSize: 9,
    },
    alternateRowStyles: {
      fillColor: [245, 247, 250],
    },
    margin: { top: 10, right: 10, bottom: 10, left: 10 },
    didDrawPage: (data) => {
      // Footer with page number
      const pageCount = doc.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Pagina ${data.pageNumber} de ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    },
  });

  // Download
  const dateStr = format(new Date(), 'yyyy-MM-dd');
  doc.save(`${filename}_${dateStr}.pdf`);
}

// ============== COMMON FORMATTERS ==============

export const formatters = {
  date: (value: string | null) =>
    value ? format(new Date(value), 'dd/MM/yyyy') : '',

  dateTime: (value: string | null) =>
    value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : '',

  currency: (value: number | null) =>
    value != null ? `$${value.toLocaleString('es-ES', { minimumFractionDigits: 2 })}` : '',

  number: (value: number | null) =>
    value != null ? value.toLocaleString('es-ES') : '',

  percent: (value: number | null) =>
    value != null ? `${value.toFixed(1)}%` : '',

  boolean: (value: boolean | null) =>
    value === true ? 'Si' : value === false ? 'No' : '',

  status: (value: string | null) => {
    const labels: Record<string, string> = {
      pending: 'Pendiente',
      in_progress: 'En Progreso',
      completed: 'Completado',
      cancelled: 'Cancelado',
      passed: 'Aprobado',
      failed: 'Rechazado',
    };
    return value ? labels[value] || value : '';
  },

  priority: (value: string | null) => {
    const labels: Record<string, string> = {
      urgent: 'Urgente',
      high: 'Alta',
      normal: 'Normal',
      low: 'Baja',
    };
    return value ? labels[value] || value : '';
  },
};

// ============== PREDEFINED EXPORT CONFIGS ==============

export const workOrdersExportConfig: ExportColumn[] = [
  { key: 'order_number', header: 'N° Orden', width: 15 },
  { key: 'product_name', header: 'Producto', width: 25 },
  { key: 'customer_name', header: 'Cliente', width: 20 },
  { key: 'quantity', header: 'Cantidad', width: 10, format: formatters.number },
  { key: 'status', header: 'Estado', width: 12, format: formatters.status },
  { key: 'priority', header: 'Prioridad', width: 12, format: formatters.priority },
  { key: 'due_date', header: 'Fecha Entrega', width: 12, format: formatters.date },
  { key: 'created_at', header: 'Creado', width: 12, format: formatters.date },
];

export const qualityInspectionsExportConfig: ExportColumn[] = [
  { key: 'inspection_number', header: 'N° Inspeccion', width: 15 },
  { key: 'work_order_number', header: 'Orden', width: 15 },
  { key: 'inspector_name', header: 'Inspector', width: 20 },
  { key: 'status', header: 'Resultado', width: 12, format: formatters.status },
  { key: 'inspection_date', header: 'Fecha', width: 12, format: formatters.date },
  { key: 'notes', header: 'Observaciones', width: 30 },
];

export const productionBatchesExportConfig: ExportColumn[] = [
  { key: 'batch_number', header: 'N° Lote', width: 15 },
  { key: 'work_order_number', header: 'Orden', width: 15 },
  { key: 'quantity', header: 'Cantidad', width: 10, format: formatters.number },
  { key: 'status', header: 'Estado', width: 12, format: formatters.status },
  { key: 'created_at', header: 'Fecha', width: 12, format: formatters.date },
];

export const machinesExportConfig: ExportColumn[] = [
  { key: 'name', header: 'Nombre', width: 20 },
  { key: 'code', header: 'Codigo', width: 12 },
  { key: 'type', header: 'Tipo', width: 15 },
  { key: 'model', header: 'Modelo', width: 15 },
  { key: 'is_active', header: 'Activa', width: 10, format: formatters.boolean },
  { key: 'capacity_per_hour', header: 'Capacidad/h', width: 12, format: formatters.number },
  { key: 'last_maintenance', header: 'Ult. Mant.', width: 12, format: formatters.date },
];

export const materialsExportConfig: ExportColumn[] = [
  { key: 'code', header: 'Codigo', width: 15 },
  { key: 'name', header: 'Nombre', width: 25 },
  { key: 'category', header: 'Categoria', width: 15 },
  { key: 'current_stock', header: 'Stock Actual', width: 12, format: formatters.number },
  { key: 'min_stock', header: 'Stock Min.', width: 12, format: formatters.number },
  { key: 'unit', header: 'Unidad', width: 10 },
  { key: 'location', header: 'Ubicacion', width: 15 },
];
